import type { AIJobStatusPayload } from '@/types/ai';

export const DEFAULT_PREVIEW_ASPECT_RATIO = 16 / 9;

export type GenerationProgressPhase = 'progress' | 'finishing';

export interface NormalizedGenerationProgressState {
  progress: number;
  phase: GenerationProgressPhase;
  message?: string;
  estimatedRemainingSeconds?: number;
  pollIntervalMs?: number;
  isCompleted: boolean;
  isFailed: boolean;
  errorMessage?: string;
}

function clampProgress(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function clampProgressValue(value: number): number {
  return Math.max(0, Math.min(100, value));
}

function normalizeStatus(value: number | string | undefined): string | number | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    if (/^\d+$/.test(trimmed)) {
      return Number(trimmed);
    }
    return trimmed;
  }
  return value;
}

export function isTerminalGenerationStatus(value: number | string | undefined): boolean {
  const normalizedStatus = normalizeStatus(value);
  return (
    normalizedStatus === 'completed' ||
    normalizedStatus === 'failed' ||
    normalizedStatus === 'cancelled' ||
    normalizedStatus === 3 ||
    normalizedStatus === 4 ||
    normalizedStatus === 5
  );
}

function readPositiveNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

interface StepSmoothedGenerationProgressInput {
  displayedProgress: number;
  confirmedProgress: number;
  elapsedMs: number;
  phase?: GenerationProgressPhase;
  estimatedRemainingSeconds?: number;
  pollIntervalMs?: number;
}

export function resolveGenerationPollIntervalMs(
  serverPollIntervalMs: number | null | undefined,
  fallbackMs: number
): number {
  return readPositiveNumber(serverPollIntervalMs) ?? fallbackMs;
}

function resolveProgressLead(progress: number, pollIntervalMs?: number): number {
  const normalizedPollInterval = resolveGenerationPollIntervalMs(pollIntervalMs, 1800);
  const pollBoost = Math.min(1.25, Math.max(0, (normalizedPollInterval - 1800) / 3200));

  if (progress < 40) return 7 + pollBoost * 1.5;
  if (progress < 70) return 5 + pollBoost * 1.2;
  if (progress < 88) return 3.2 + pollBoost;
  if (progress < 94) return 1.4 + pollBoost * 0.45;
  if (progress < 97) return 0.75 + pollBoost * 0.25;
  return 0.3;
}

function resolveProgressSpeedPerSecond(
  progress: number,
  estimatedRemainingSeconds?: number
): number {
  let speedPerSecond = 8;

  if (progress < 45) speedPerSecond = 10;
  else if (progress < 70) speedPerSecond = 7;
  else if (progress < 88) speedPerSecond = 4;
  else if (progress < 94) speedPerSecond = 1.8;
  else if (progress < 97) speedPerSecond = 0.8;
  else speedPerSecond = 0.25;

  if (estimatedRemainingSeconds && estimatedRemainingSeconds > 0) {
    if (estimatedRemainingSeconds <= 8 && progress < 90) {
      speedPerSecond *= 1.15;
    } else if (estimatedRemainingSeconds >= 45 && progress > 80) {
      speedPerSecond *= 0.85;
    }
  }

  return speedPerSecond;
}

export function stepSmoothedGenerationProgress({
  displayedProgress,
  confirmedProgress,
  elapsedMs,
  phase = 'progress',
  estimatedRemainingSeconds,
  pollIntervalMs,
}: StepSmoothedGenerationProgressInput): number {
  const currentDisplay = clampProgressValue(displayedProgress);
  const currentConfirmed = clampProgressValue(confirmedProgress);
  const deltaSeconds = Math.max(0, elapsedMs) / 1000;
  const floorProgress = Math.max(currentDisplay, currentConfirmed);

  if (phase === 'finishing') {
    const remaining = 100 - floorProgress;
    if (remaining <= 0) return 100;
    const finishingStep = Math.max(remaining * 0.55, 18) * deltaSeconds;
    return clampProgressValue(Math.min(100, floorProgress + finishingStep));
  }

  const ceilingProgress = Math.min(
    99.2,
    Math.max(currentConfirmed, currentConfirmed + resolveProgressLead(currentConfirmed, pollIntervalMs))
  );
  const remaining = ceilingProgress - floorProgress;
  if (remaining <= 0 || deltaSeconds === 0) {
    return floorProgress;
  }

  const step = resolveProgressSpeedPerSecond(floorProgress, estimatedRemainingSeconds) * deltaSeconds;
  return clampProgressValue(Math.min(ceilingProgress, floorProgress + step));
}

export interface SmoothedProgressState {
  /** 当前平滑值所属的任务身份，null 表示未绑定任务 */
  key: string | null;
  value: number;
}

/**
 * 平滑值以已显示进度为下限单调递增，因此复用同一个组件实例展示不同任务时
 * 必须按任务身份重置，否则后一个任务会被钉在前一个任务的百分比上。
 */
export function resolveSmoothedProgressState(
  previous: SmoothedProgressState,
  next: { key: string | null; confirmedProgress: number }
): SmoothedProgressState {
  if (previous.key === next.key) return previous;
  return { key: next.key, value: clampProgressValue(next.confirmedProgress) };
}

export function normalizeAspectRatio(
  input: number | string | null | undefined,
  fallback = DEFAULT_PREVIEW_ASPECT_RATIO
): number {
  if (typeof input === 'number') {
    return Number.isFinite(input) && input > 0 ? input : fallback;
  }

  if (typeof input !== 'string') {
    return fallback;
  }

  const trimmed = input.trim();
  if (!trimmed) return fallback;

  const directValue = Number(trimmed);
  if (Number.isFinite(directValue) && directValue > 0) {
    return directValue;
  }

  const match = trimmed.match(/^(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)$/);
  if (!match) return fallback;

  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return fallback;
  }

  return width / height;
}

export function resolveGenerationProgressState(
  payload: AIJobStatusPayload,
  previousProgress = 0
): NormalizedGenerationProgressState {
  const normalizedStatus = normalizeStatus(payload.status);
  const rawProgress =
    typeof payload.calculatedProgress === 'number' && Number.isFinite(payload.calculatedProgress)
      ? payload.calculatedProgress
      : typeof payload.progress === 'number' && Number.isFinite(payload.progress)
        ? payload.progress
        : undefined;

  const isFailed =
    normalizedStatus === 'failed' ||
    normalizedStatus === 'cancelled' ||
    normalizedStatus === 4 ||
    normalizedStatus === 5;
  const isCompleted = normalizedStatus === 'completed' || normalizedStatus === 3;
  const nextProgress = clampProgress(
    Math.max(previousProgress, rawProgress ?? (isCompleted ? 100 : previousProgress))
  );

  const message =
    typeof payload.message === 'string' && payload.message.trim()
      ? payload.message.trim()
      : isCompleted
        ? '生成完成，正在加载最新内容...'
        : undefined;
  const errorMessage =
    typeof payload.errorMessage === 'string' && payload.errorMessage.trim()
      ? payload.errorMessage.trim()
      : typeof payload.error === 'string' && payload.error.trim()
        ? payload.error.trim()
        : undefined;

  return {
    progress: isCompleted ? 100 : nextProgress,
    phase: isCompleted ? 'finishing' : 'progress',
    message,
    estimatedRemainingSeconds: readPositiveNumber(payload.estimatedRemainingSeconds),
    pollIntervalMs: readPositiveNumber(payload.pollIntervalMs),
    isCompleted,
    isFailed,
    errorMessage,
  };
}
